import type { Mode } from "@eldrun/content";
import { hash32, mix32 } from "@eldrun/shared";
import { buildHeightfield, type Heightfield, sampleHeight } from "./heightfield.ts";
import { WORLD_HALF } from "./params.ts";
import { planWorld, type Plan } from "./planner.ts";
import { GEN_VERSION } from "./version.ts";

/** Resumo enviado pelo servidor; o cliente recalcula e recusa a partida se o hash divergir. */
export interface WorldDescriptor {
  genVersion: number;
  seed: number;
  mode: Mode;
  attempt: number;
  seaBaseT: number;
  landScale: number;
  worldHash: number;
}

export interface World {
  plan: Plan;
  heightfield: Heightfield;
  descriptor: WorldDescriptor;
}

const f32 = new Float32Array(1);
const f32Bits = new Uint32Array(f32.buffer);
const f64 = new Float64Array(1);
const f64Bits = new Uint32Array(f64.buffer);

function floatBits(value: number): number {
  f32[0] = value;
  return f32Bits[0] as number;
}

function doubleBits(value: number): [number, number] {
  f64[0] = value;
  return [f64Bits[0] as number, f64Bits[1] as number];
}

/** 64 sondas de altura numa grade 8 × 8 sobre a ilha: detectam divergência de ponto flutuante. */
export function probeHeights(hf: Heightfield): number[] {
  const probes: number[] = [];
  const span = WORLD_HALF * 0.9;
  for (let j = 0; j < 8; j++) {
    for (let i = 0; i < 8; i++) {
      const x = -span + (2 * span * (i + 0.37)) / 8;
      const z = -span + (2 * span * (j + 0.61)) / 8;
      probes.push(sampleHeight(hf, x, z));
    }
  }
  return probes;
}

/** Incorpora todas as palavras de 32 bits de um buffer ao hash, em ordem fixa. */
function hashWords(seed: number, view: ArrayBufferView): number {
  const bytes = view.byteLength;
  const words = new Uint32Array(view.buffer, view.byteOffset, bytes >>> 2);
  let h = mix32(seed ^ bytes);
  for (let i = 0; i < words.length; i++) h = mix32((h ^ (words[i] as number)) + 0x9e3779b9);
  const tail = new Uint8Array(view.buffer, view.byteOffset + (bytes & ~3), bytes & 3);
  for (let i = 0; i < tail.length; i++) h = mix32((h ^ (tail[i] as number)) + 0x7f4a7c15);
  return h;
}

/** Partes do plano que entram no hash (o campo de ruído não é necessário). */
export type HashablePlan = Pick<
  Plan,
  "seed" | "attempt" | "seaBaseT" | "landScale" | "stats" | "heights" | "moisture" | "coastDistance" | "landDistance" | "biome" | "pois"
>;

/**
 * Hash do mundo: escalares do plano, todas as grades do plano e o heightfield inteiro. Qualquer
 * diferença de um bit entre cliente e servidor muda o resultado.
 */
export function computeWorldHash(plan: HashablePlan, hf: Heightfield): number {
  let h = hash32(
    GEN_VERSION,
    plan.seed,
    plan.attempt,
    ...doubleBits(plan.seaBaseT),
    ...doubleBits(plan.landScale),
    plan.stats.landCells,
    ...plan.stats.biomeCells,
    ...probeHeights(hf).map(floatBits),
  );
  // POIs do descritor (tipo, posição, giro, carimbo e dado): cliente e servidor planejam igual.
  for (const p of plan.pois) h = hash32(h, p.id, p.type.length, p.type.charCodeAt(0), floatBits(p.x), floatBits(p.z), floatBits(p.rot), floatBits(p.target), p.data);
  for (const grid of [plan.heights, plan.moisture, plan.coastDistance, plan.landDistance, plan.biome, hf.heights]) {
    h = hashWords(h, grid);
  }
  return h;
}

/** Gera a ilha completa: plano, heightfield físico e descritor com `worldHash`. */
export function generateWorld(seed: number, mode: Mode): World {
  const plan = planWorld(seed >>> 0, mode);
  const heightfield = buildHeightfield(plan.field);
  return {
    plan,
    heightfield,
    descriptor: {
      genVersion: GEN_VERSION,
      seed: plan.seed,
      mode,
      attempt: plan.attempt,
      seaBaseT: plan.seaBaseT,
      landScale: plan.landScale,
      worldHash: computeWorldHash(plan, heightfield),
    },
  };
}
